import type { LocaleLanguage, LocalePreferences, TimezoneOverride } from '@molt/shared';
import { parseCardTimestamp } from './card-dates';

/**
 * Mirrors `CardDateTranslate` (`lib/card-dates.ts:20`) and through it
 * `AttachmentTranslate` and `ChecklistTranslate`, so this module stays decoupled
 * from the interface-language context — a non-component module takes a
 * translator as a parameter rather than importing the React context.
 *
 * The `count?` third parameter is carried even though this module declares no
 * plural: every sibling declaration has it, and dropping it would make the type
 * structurally incompatible with `InterfaceLanguageContextValue['t']` at every
 * call site.
 */
export type LocaleFormattingTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

/**
 * `LocaleLanguage` → catalogue key, in one place. The union's members carry a
 * hyphen (`en-US`) and the catalogue's key grammar is dotted, so the two
 * spellings have to meet somewhere; here, once, rather than at each call site.
 *
 * `Record<LocaleLanguage, string>` is what makes a ninth language a compile
 * error rather than a silent omission: `tsc` refuses the object literal if a
 * member of the union has no entry, and `LocaleLanguage` is `z.infer` of
 * `SUPPORTED_LOCALE_LANGUAGES`, so adding one in `@molt/shared` fails here.
 */
const LANGUAGE_KEYS: Record<LocaleLanguage, string> = {
  en: 'profile.locale.language.en',
  'en-US': 'profile.locale.language.enUS',
  'en-GB': 'profile.locale.language.enGB',
  fr: 'profile.locale.language.fr',
  de: 'profile.locale.language.de',
  es: 'profile.locale.language.es',
  'pt-BR': 'profile.locale.language.ptBR',
  ja: 'profile.locale.language.ja',
};

/**
 * The first day of the week, as a `Date#getDay` index: `0` Sunday, `1` Monday.
 * Only those two occur across `SUPPORTED_LOCALE_LANGUAGES`, so the union is
 * narrowed to them rather than opened to all seven -- a narrower type is a
 * smaller thing for a caller to switch on, and it stays assignable to
 * `lib/board-calendar.ts`'s wider `CalendarWeekStartsOn` if that module is ever
 * taught to derive its hard-coded default from here.
 */
export type WeekStart = 0 | 1;

/**
 * `FR-NOTIF-032` -- "A per-user language setting drives date, time and calendar
 * formatting: day-first versus month-first, 12- versus 24-hour, **and first day
 * of week**." Its edge case settles where the value comes from, and it is the
 * reason this is a derivation and not a setting:
 *
 * > Date-format preference is derived from the stored language/locale; there is
 * > no separate date-format setting unless the product requirement is changed.
 *
 * So the week start is DERIVED from the language the member has already chosen.
 * A second stored preference beside `user_preferences.language` is not merely
 * unnecessary here -- the requirement forbids it, and the live catalogue agrees:
 * `user_preferences` carries `language` and `timezone_override` and no
 * `week_start` column to read even if one were wanted.
 *
 * `Record<LocaleLanguage, WeekStart>` for exactly the reason `LANGUAGE_KEYS`
 * above is one: `tsc` refuses the object literal if a member of the union has
 * no entry, so a ninth language added in `@molt/shared` is a compile error here
 * rather than a silent fallback to Monday.
 *
 * A static record rather than `Intl.Locale#getWeekInfo()`: that API is not
 * reliably present across the runtimes this package targets, and a value the
 * host decides cannot be asserted in a unit test.
 *
 * WARNING -- bare `en` is MONDAY, and that is a continuity decision rather than
 * a CLDR one. `DEFAULT_LOCALE_PREFERENCES.language` is `en`, and every calendar
 * shipped so far starts on Monday (`lib/board-calendar.ts` hard-codes
 * `weekStartsOn = 1`). Mapping `en` to Sunday would silently reorder the week
 * for every member who has never opened the language control. `en-US` carries
 * the Sunday case explicitly, which is where a US member's expectation is
 * actually expressed.
 */
const WEEK_STARTS: Record<LocaleLanguage, WeekStart> = {
  en: 1,
  'en-US': 0,
  'en-GB': 1,
  fr: 1,
  de: 1,
  es: 1,
  'pt-BR': 0,
  ja: 0,
};

export function weekStartForLocale(language: LocaleLanguage): WeekStart {
  return WEEK_STARTS[language];
}

/**
 * One constant for three throw sites — `dateTimeLocalInZoneToIso` throws once
 * directly and the private `parseDateTimeLocal` twice. Naming the key once is
 * what stops three throws drifting into three spellings; resolving it lazily,
 * at the throw rather than on entry, is what keeps a successful conversion from
 * touching the catalogue at all.
 */
const INVALID_DATE_TIME_KEY = 'primitives.localeFormatting.invalidDateTime';

const FALLBACK_TIMEZONE = 'UTC';
const DATE_TIME_LOCAL_RE =
  /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2})(?::(?<second>\d{2}))?$/;

export function effectiveTimezone(preferences: LocalePreferences): string {
  if (preferences.timezoneOverride !== null) return preferences.timezoneOverride;

  const timezone =
    typeof Intl === 'undefined'
      ? undefined
      : new Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone && isSupportedTimezone(timezone) ? timezone : FALLBACK_TIMEZONE;
}

export function formatLocaleCardDate(
  t: LocaleFormattingTranslate,
  value: string,
  preferences: LocalePreferences,
): string {
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return t('primitives.cardDate.unknown');

  return new Intl.DateTimeFormat(preferences.language, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: effectiveTimezone(preferences),
  }).format(parsed);
}

/**
 * BE-44.7 (FR-EXP-029 edge case): a due date that may be date-only. An
 * all-day due is stored midnight-UTC-normalised, so it formats with
 * `timeZone: 'UTC'` and no time fields — the same calendar date for every
 * viewer, in their own language. Timed dues keep `formatLocaleCardDate`'s
 * instant-in-effective-timezone rendering.
 */
export function formatLocaleCardDueDate(
  t: LocaleFormattingTranslate,
  value: string,
  preferences: LocalePreferences,
  dueAllDay: boolean,
): string {
  if (!dueAllDay) return formatLocaleCardDate(t, value, preferences);
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return t('primitives.cardDate.unknown');

  return new Intl.DateTimeFormat(preferences.language, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

export function formatLocaleDateTimeLabel(
  t: LocaleFormattingTranslate,
  value: string,
  preferences: LocalePreferences,
): string {
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return t('primitives.cardDate.unknown');

  return new Intl.DateTimeFormat(preferences.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: effectiveTimezone(preferences),
  }).format(parsed);
}

export function formatDateTimeLocalInZone(
  value: string | null,
  preferences: LocalePreferences,
): string {
  if (value === null) return '';
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return '';

  const parts = localParts(parsed.getTime(), effectiveTimezone(preferences));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * ⚠ Throws copy a user reads. `CardDatesSection`'s `saveDates` renders
 * `cause.message` into the section's error slot, so this message is visible
 * product copy and is routed — unlike `lib/card-dates.ts`'s byte-identical
 * `dateTimeLocalToIso` throw, whose two catch sites both discard the message.
 * Read the catch site; the two sibling leaves answer differently.
 *
 * The three throw sites share `INVALID_DATE_TIME_KEY` rather than a resolved
 * string, so they cannot drift into three spellings AND a successful conversion
 * never calls the translator at all.
 */
export function dateTimeLocalInZoneToIso(
  t: LocaleFormattingTranslate,
  value: string,
  preferences: LocalePreferences,
): string {
  const target = parseDateTimeLocal(t, value);
  const timezone = effectiveTimezone(preferences);
  const targetAsUtc = Date.UTC(
    target.year,
    target.month - 1,
    target.day,
    target.hour,
    target.minute,
    target.second,
  );

  let instant = targetAsUtc;
  for (let i = 0; i < 4; i += 1) {
    const offset = timezoneOffsetMs(instant, timezone);
    const next = targetAsUtc - offset;
    if (next === instant) break;
    instant = next;
  }

  const resolved = localParts(instant, timezone);
  if (
    Number(resolved.year) !== target.year ||
    Number(resolved.month) !== target.month ||
    Number(resolved.day) !== target.day ||
    Number(resolved.hour) !== target.hour ||
    Number(resolved.minute) !== target.minute ||
    Number(resolved.second) !== target.second
  ) {
    throw new Error(t(INVALID_DATE_TIME_KEY));
  }

  return new Date(instant).toISOString();
}

export function languageLabel(
  t: LocaleFormattingTranslate,
  language: LocaleLanguage,
): string {
  return t(LANGUAGE_KEYS[language]);
}

/**
 * Only the null branch is copy. The other returns the raw IANA identifier
 * (`Asia/Tokyo`), which stays literal in every locale — an identifier, not a
 * sentence.
 */
export function timezoneLabel(
  t: LocaleFormattingTranslate,
  timezoneOverride: TimezoneOverride,
): string {
  return timezoneOverride ?? t('profile.locale.deviceTimezone');
}

function parseDateTimeLocal(
  t: LocaleFormattingTranslate,
  value: string,
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const match = DATE_TIME_LOCAL_RE.exec(value.trim());
  if (!match?.groups) throw new Error(t(INVALID_DATE_TIME_KEY));

  const parts = {
    year: Number(match.groups.year),
    month: Number(match.groups.month),
    day: Number(match.groups.day),
    hour: Number(match.groups.hour),
    minute: Number(match.groups.minute),
    second: Number(match.groups.second ?? '00'),
  };
  const utc = new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    ),
  );

  if (
    utc.getUTCFullYear() !== parts.year ||
    utc.getUTCMonth() !== parts.month - 1 ||
    utc.getUTCDate() !== parts.day ||
    parts.hour > 23 ||
    parts.minute > 59 ||
    parts.second > 59
  ) {
    throw new Error(t(INVALID_DATE_TIME_KEY));
  }

  return parts;
}

function localParts(
  instant: number,
  timezone: string,
): {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone: timezone,
  }).formatToParts(new Date(instant));

  return {
    year: part(parts, 'year'),
    month: part(parts, 'month'),
    day: part(parts, 'day'),
    hour: part(parts, 'hour'),
    minute: part(parts, 'minute'),
    second: part(parts, 'second'),
  };
}

function timezoneOffsetMs(instant: number, timezone: string): number {
  const parts = localParts(instant, timezone);
  const localAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return localAsUtc - instant;
}

function part(parts: Intl.DateTimeFormatPart[], type: string): string {
  const value = parts.find((entry) => entry.type === type)?.value;
  if (value === undefined) throw new RangeError(`Missing ${type} date-time part`);
  return value;
}

function isSupportedTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}
